import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertTriangle, ArrowLeft } from "lucide-react";
import { userApi } from "../lib/api";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2500;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, order_id: null, amount: null });
  const attemptsRef = useRef(0);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", payment_status: null, order_id: null });
      return;
    }

    const poll = async () => {
      if (stopRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await userApi.get(`/payments/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          stopRef.current = true;
          setState({ status: "paid", payment_status: "paid", order_id: data.order_id, amount: data.amount_total });
          sessionStorage.removeItem("dnj_pending_order_id");
          return;
        }
        if (data.status === "expired") {
          stopRef.current = true;
          setState({ status: "expired", payment_status: "expired", order_id: data.order_id });
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          stopRef.current = true;
          setState({ status: "timeout", payment_status: data.payment_status, order_id: data.order_id });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attemptsRef.current >= MAX_POLLS) {
          stopRef.current = true;
          setState({ status: "error", payment_status: null });
        } else {
          setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();
    return () => { stopRef.current = true; };
  }, [sessionId]);

  let icon, title, message, color;
  if (state.status === "polling") {
    icon = <Loader2 className="w-14 h-14 animate-spin" strokeWidth={3} />;
    title = "Verifica pagamento in corso...";
    message = "Stiamo confermando il pagamento con Stripe.";
    color = "text-yellow-400";
  } else if (state.status === "paid") {
    icon = <Check className="w-14 h-14" strokeWidth={4} />;
    title = "Pagamento confermato";
    message = `Ordine #${(state.order_id || "").slice(0, 8).toUpperCase()} ricevuto. Riceverai una mail di conferma a breve.`;
    color = "text-yellow-400";
  } else if (state.status === "expired") {
    icon = <AlertTriangle className="w-14 h-14" strokeWidth={3} />;
    title = "Sessione scaduta";
    message = "La sessione di pagamento è scaduta. Puoi riprovare.";
    color = "text-red-400";
  } else if (state.status === "timeout") {
    icon = <AlertTriangle className="w-14 h-14" strokeWidth={3} />;
    title = "Verifica in ritardo";
    message = "Non riusciamo a confermare subito il pagamento. Riceverai aggiornamenti via email.";
    color = "text-yellow-400";
  } else {
    icon = <AlertTriangle className="w-14 h-14" strokeWidth={3} />;
    title = "Sessione mancante";
    message = "Nessun session id trovato.";
    color = "text-red-400";
  }

  return (
    <div className="bg-zinc-950 min-h-screen text-white">
      <Navbar />
      <main className="pt-28 pb-20 min-h-[60vh] flex items-center justify-center px-6">
        <div data-testid="checkout-success-page" className="max-w-xl w-full bg-zinc-900 border-4 border-yellow-400 brutal-shadow p-10 text-center">
          <div className={`inline-flex items-center justify-center bg-yellow-400 text-black w-24 h-24 mb-6 brutal-shadow-black ${state.status === "polling" ? "" : ""}`}>
            <div className={color === "text-yellow-400" ? "text-black" : "text-black"}>{icon}</div>
          </div>
          <h1 className="font-heading text-4xl uppercase text-white mb-3">{title}</h1>
          <p className="text-zinc-400 mb-2">{message}</p>
          {state.order_id && (
            <p className="text-zinc-500 text-sm">ID: <span className="text-yellow-400 font-mono">#{state.order_id.slice(0, 8).toUpperCase()}</span></p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <Link to="/account" data-testid="success-go-account"
              className="bg-yellow-400 text-black font-heading uppercase px-6 py-3 hover:bg-white transition">
              I miei ordini
            </Link>
            <Link to="/" data-testid="success-go-home"
              className="border-2 border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 font-heading uppercase px-6 py-3 transition flex items-center gap-2 justify-center">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
